import { Box } from "@mui/material"
import React, { memo, useEffect, useRef } from "react"
import { BoxPlotChart, ViolinChart } from "@sgratzl/chartjs-chart-boxplot"
import { color_tones, lighter_rgba, to_rgba } from "./const"
import { formatValuePythonic } from "@utils/format/pythonicFormat"

type InterquartileRangeProps = {
  data: number[][]
  dataSecondYear: number[]
  minValue: number
  maxValue: number
  isViolinChart: boolean
  axisFormat?: string
}
export const InterquartileRange = memo(function InterquartileRange({
  data,
  dataSecondYear,
  minValue,
  maxValue,
  isViolinChart,
  axisFormat,
}: InterquartileRangeProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const canvasRef = useRef<any | null>(null)

  useEffect(() => {
    if (!data?.length) {
      return
    }

    const sorted: number[][] = []

    data.forEach((item) => {
      const sortedCopy = item.slice().sort(function (a, b) {
        return a - b
      })
      sorted.push(sortedCopy)
    })
    const sortedSecondYear = dataSecondYear.slice().sort(function (a, b) {
      return a - b
    })

    const isSecondYearProvided = !!dataSecondYear?.length

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    const boxplotData: { labels: any[]; datasets: any[] } = {
      labels: [""],
      datasets: [],
    }

    sorted.forEach((item, index) => {
      boxplotData.datasets.push({
        label: `IQR ${index}`,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[index], 0, 0.5)),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[index], 0, 1)),
        borderWidth: 1,
        outlierColor: "#999999",
        padding: 10,
        itemRadius: 1,
        maxBarThickness: 60,
        data: [item || []],
      })
    })

    if (isSecondYearProvided) {
      boxplotData.datasets.push({
        label: "IQR Additional Year",
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        backgroundColor: to_rgba(lighter_rgba(color_tones[10], 0, 0.5)),
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        borderColor: to_rgba(lighter_rgba(color_tones[10], 0, 1)),
        borderWidth: 1,
        outlierColor: "#999999",
        padding: 10,
        itemRadius: 1,
        maxBarThickness: 60,
        data: [sortedSecondYear || []],
      })
    }

    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (context) {
      if (canvasRef.current.chart) {
        canvasRef.current.chart.destroy()
      }

      const config = {
        data: boxplotData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: "y",
          animation: {
            duration: 0,
          },
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              min: minValue,
              max: maxValue,
              ticks: {
                callback: function (value: number) {
                  return formatValuePythonic(value, axisFormat)
                },
              },
            },
          },
        },
      }

      canvasRef.current.chart = isViolinChart
        ? // @ts-ignore
          new ViolinChart(context, config)
        : // @ts-ignore
          new BoxPlotChart(context, config)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [data, dataSecondYear, isViolinChart])

  return (
    <Box sx={{ height: "250px", padding: "10px" }}>
      <canvas id="canvas" ref={canvasRef}></canvas>
    </Box>
  )
})
